import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Modal } from 'react-bootstrap'
import { KTSVG } from '_metronic/helpers'
import { useAuthStore } from 'Store/Auth'
import { shallow } from 'zustand/shallow'

interface Props {
  isOpen: boolean
  documentNo?: string
  isSubmitting?: boolean
  onClose: () => void
  onSubmit: (file: File, signedByName: string) => void
}

const CANVAS_HEIGHT = 220

const UploadSignedModal: React.FC<Props> = ({
  isOpen,
  documentNo,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const modalsRoot = document.getElementById('root-modals') || document.body
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const [hasStroke, setHasStroke] = useState(false)
  const [signedByName, setSignedByName] = useState('')
  const profileFullName = useAuthStore(state => state.profile?.fullName ?? '', shallow)

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    setHasStroke(false)
  }, [])

  const resetForm = useCallback(() => {
    clearCanvas()
    setSignedByName('')
  }, [clearCanvas])

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const parent = canvas.parentElement
    const width = Math.max(parent?.clientWidth ?? 560, 280)
    const dpr = window.devicePixelRatio || 1
    canvas.width = Math.floor(width * dpr)
    canvas.height = Math.floor(CANVAS_HEIGHT * dpr)
    canvas.style.width = `${width}px`
    canvas.style.height = `${CANVAS_HEIGHT}px`
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111827'
    ctx.lineWidth = 2.5
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, CANVAS_HEIGHT)
    setHasStroke(false)
  }, [])

  useEffect(() => {
    if (!isOpen) {
      resetForm()
      return
    }
    if (profileFullName) {
      setSignedByName(profileFullName)
    }
    const frame = requestAnimationFrame(() => resizeCanvas())
    window.addEventListener('resize', resizeCanvas)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', resizeCanvas)
    }
  }, [isOpen, resizeCanvas, resetForm, profileFullName])

  const getPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (isSubmitting) return
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    drawingRef.current = true
    canvas.setPointerCapture(e.pointerId)
    const { x, y } = getPoint(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || isSubmitting) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = getPoint(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    if (!hasStroke) setHasStroke(true)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    drawingRef.current = false
    const canvas = canvasRef.current
    if (canvas?.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    resetForm()
    onClose()
  }

  const trimmedName = signedByName.trim()
  const canSubmit = hasStroke && trimmedName.length > 0 && !isSubmitting

  const handleSubmit = () => {
    const canvas = canvasRef.current
    if (!canvas || !canSubmit) return
    canvas.toBlob(blob => {
      if (!blob) return
      const fileName = `signed-${documentNo || 'document'}.png`
      onSubmit(new File([blob], fileName, { type: 'image/png' }), trimmedName)
    }, 'image/png')
  }

  return createPortal(
    <Modal
      id='kt_modal_upload_signed'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-650px'
      show={isOpen}
      onHide={handleClose}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2>ลงลายเซ็น{documentNo ? ` — ${documentNo}` : ''}</h2>
          {!isSubmitting && (
            <div className='btn btn-sm btn-icon btn-active-light-danger' onClick={handleClose}>
              <KTSVG className='svg-icon-1' path='/media/icons/duotune/general/gen040.svg' />
            </div>
          )}
        </div>

        <div className='modal-body'>
          <div className='mb-5'>
            <label className='form-label fw-bold required'>ชื่อ</label>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='ชื่อตัวบรรจงผู้ลงนาม'
              value={signedByName}
              disabled={isSubmitting}
              onChange={e => setSignedByName(e.target.value)}
            />
          </div>

          <p className='text-gray-600 fs-7 mb-3'>วาดลายเซ็นในกรอบด้านล่าง แล้วกดบันทึกเพื่ออัปโหลด</p>
          <div
            className='border border-gray-300 rounded bg-white overflow-hidden'
            style={{ touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              className='d-block w-100'
              style={{
                height: CANVAS_HEIGHT,
                cursor: isSubmitting ? 'not-allowed' : 'crosshair',
                opacity: isSubmitting ? 0.6 : 1,
              }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            />
          </div>
        </div>

        <div className='modal-footer justify-content-between'>
          <button
            type='button'
            className='btn btn-light fw-bold'
            disabled={isSubmitting || !hasStroke}
            onClick={clearCanvas}>
            ล้าง
          </button>
          <div className='d-flex gap-2'>
            <button
              type='button'
              className='btn btn-light fw-bold'
              disabled={isSubmitting}
              onClick={handleClose}>
              ยกเลิก
            </button>
            <button
              type='button'
              className='btn btn-primary fw-bold'
              disabled={!canSubmit}
              onClick={handleSubmit}>
              {isSubmitting ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' />
                  กำลังอัปโหลด...
                </>
              ) : (
                'บันทึก'
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export default UploadSignedModal
